<template>
    <v-card class="sales-interface" outlined>
        <v-card-text>
            <div class="heading">
                <!-- Product Types -->
                <v-tabs v-model="activeProductType" background-color="transparent">
                    <v-tab v-for="(productType, index) in productTypes" :key="index" :href="`#${productType}`">
                        {{ $t(`general.${productType}`) }}
                    </v-tab>
                </v-tabs>

                <!-- Add Sector/Table Buttons -->
                <div class="buttons" :class="{ wfull: $vuetify.breakpoint.mdAndDown }">
                    <v-autocomplete
                        ref="selCategory"
                        v-model="categoryToAdd"
                        :items="availableCategories"
                        item-text="name"
                        :label="$t('general.add_category')"
                        :disabled="!availableCategories.length"
                        :no-data-text="$t('order_settings.no_match_found')"
                        return-object
                        class="mr-3 maxw250"
                        @change="addCategory"
                    />

                    <v-autocomplete
                        ref="selProduct"
                        v-model="productToAdd"
                        class="mr-3 maxw250"
                        :items="availableProducts"
                        item-text="name"
                        :label="$t('order_settings.add_product')"
                        :disabled="!availableProducts.length"
                        :no-data-text="$t('order_settings.no_match_found')"
                        return-object
                        @change="addProduct"
                    />

                    <v-spacer />

                    <v-btn color="primary" depressed class="minw100 mb-3" @click="autogenerate">
                        {{ $t('order_settings.autogenerate') }}
                    </v-btn>
                </div>
            </div>

            <!-- Button Layout -->
            <v-tabs-items v-model="activeProductType" touchless class="product-type-tabs">
                <v-tab-item
                    v-for="(productType, index) in productTypes"
                    :key="index"
                    class="product-type-tab"
                    touchless
                    :value="productType"
                >
                    <div
                        class="category-container"
                        :class="{ disabled: draggedButtonId && draggedButtonId.includes('productbtn') }"
                    >
                        <div v-if="grids.length" class="grid-settings">
                            <div class="row-column">
                                <v-btn
                                    text
                                    fab
                                    dark
                                    class="grid-btn"
                                    :disabled="
                                        grids[productTypes.indexOf(activeProductType)].categories_column_count <= 1 ||
                                            lastRowColumnCount.categories.last_column > 0
                                    "
                                    @click="updateGridSize('categories', 'column', -1)"
                                >
                                    <v-icon size="20">fal fa-minus</v-icon>
                                </v-btn>

                                <div class="label">{{ $t('general.columns') }}</div>

                                <v-btn text fab dark class="grid-btn" @click="updateGridSize('categories', 'column', 1)">
                                    <v-icon size="20">fal fa-plus</v-icon>
                                </v-btn>
                            </div>

                            <div class="row-column">
                                <v-btn
                                    text
                                    fab
                                    dark
                                    class="grid-btn"
                                    :disabled="
                                        grids[productTypes.indexOf(activeProductType)].categories_row_count <= 1 ||
                                            lastRowColumnCount.categories.last_row > 0
                                    "
                                    @click="updateGridSize('categories', 'row', -1)"
                                >
                                    <v-icon size="20">fal fa-minus</v-icon>
                                </v-btn>

                                <div class="label">{{ $tc('general.row', 2) }}</div>

                                <v-btn text fab dark class="grid-btn" @click="updateGridSize('categories', 'row', 1)">
                                    <v-icon size="20">fal fa-plus</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <table v-if="grids.length">
                            <tr
                                v-for="(row, rowIndex) in grids[productTypes.indexOf(productType)].categories_row_count"
                                :key="rowIndex"
                            >
                                <td
                                    v-for="(column, colIndex) in grids[productTypes.indexOf(productType)]
                                        .categories_column_count"
                                    :id="`${row}-${column}-categorycontainer`"
                                    :key="colIndex + 1000"
                                    :style="{
                                        height: 100 / grids[productTypes.indexOf(productType)].categories_row_count + '%'
                                    }"
                                    class="drop-container-category"
                                >
                                    <sales-button
                                        v-if="getCategoryButton(productType, row, column)"
                                        :id="`${row}-${column}-categorybtn`"
                                        class="category"
                                        :class="{ 'drag-active': draggedButtonId == `${row}-${column}-categorybtn` }"
                                        :selected="
                                            selectedButton &&
                                                selectedButton.id &&
                                                selectedButton.id == getCategoryButton(productType, row, column).id
                                        "
                                        :button-config="getCategoryButton(productType, row, column)"
                                        :configurable="!draggedButtonId"
                                        :images="buttonImages"
                                        @click="selectButton"
                                        @delete="deleteButton"
                                        @config-update="updateButton"
                                    />
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div
                        class="product-container"
                        :class="{ disabled: draggedButtonId && draggedButtonId.includes('categorybtn') }"
                    >
                        <div v-if="grids.length" class="grid-settings">
                            <div class="row-column">
                                <v-btn
                                    text
                                    fab
                                    dark
                                    class="grid-btn"
                                    :disabled="
                                        grids[productTypes.indexOf(activeProductType)].products_column_count <= 1 ||
                                            lastRowColumnCount.products.last_column > 0
                                    "
                                    @click="updateGridSize('products', 'column', -1)"
                                >
                                    <v-icon size="20">fal fa-minus</v-icon>
                                </v-btn>

                                <div class="label">{{ $t('general.columns') }}</div>

                                <v-btn text fab dark class="grid-btn" @click="updateGridSize('products', 'column', 1)">
                                    <v-icon size="20">fal fa-plus</v-icon>
                                </v-btn>
                            </div>

                            <div class="row-column">
                                <v-btn
                                    text
                                    fab
                                    dark
                                    class="grid-btn"
                                    :disabled="
                                        grids[productTypes.indexOf(activeProductType)].products_row_count <= 1 ||
                                            lastRowColumnCount.products.last_row > 0
                                    "
                                    @click="updateGridSize('products', 'row', -1)"
                                >
                                    <v-icon size="20">fal fa-minus</v-icon>
                                </v-btn>

                                <div class="label">{{ $tc('general.row', 2) }}</div>

                                <v-btn text fab dark class="grid-btn" @click="updateGridSize('products', 'row', 1)">
                                    <v-icon size="20">fal fa-plus</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <table v-if="grids.length">
                            <tr
                                v-for="(row, rowIndex) in grids[productTypes.indexOf(productType)].products_row_count"
                                :key="rowIndex"
                            >
                                <td
                                    v-for="(column, colIndex) in grids[productTypes.indexOf(productType)]
                                        .products_column_count"
                                    :id="`${row}-${column}-productcontainer`"
                                    :key="colIndex + 1000"
                                    :style="{
                                        height: 100 / grids[productTypes.indexOf(productType)].products_row_count + '%'
                                    }"
                                    class="drop-container-product"
                                >
                                    <sales-button
                                        v-if="getProductButton(productType, row, column)"
                                        :id="`${row}-${column}-productbtn`"
                                        class="product"
                                        :class="{ 'drag-active': draggedButtonId == `${row}-${column}-productbtn` }"
                                        :selected="
                                            selectedButton &&
                                                selectedButton.id &&
                                                selectedButton.id == getProductButton(productType, row, column).id
                                        "
                                        :button-config="getProductButton(productType, row, column)"
                                        :configurable="!draggedButtonId"
                                        :images="buttonImages"
                                        @click="selectButton"
                                        @delete="deleteButton"
                                        @config-update="updateButton"
                                    />
                                </td>
                            </tr>
                        </table>
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </v-card-text>
    </v-card>
</template>

<script>
import SalesButton from '@/components/SalesButton';
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';

export default {
    beforeRouteEnter: (to, from, next) => {
        loadData('/admin/load/sales-interface').then(data => next(vm => vm.onDataLoaded(data)));
    },

    components: {
        SalesButton
    },

    beforeDestroy() {
        interact('.drop-container-category').unset();
        interact('.drop-container-product').unset();
        interact('.draggable.category').unset();
        interact('.draggable.product').unset();
    },

    mounted() {
        // initiate drag & drop functionality
        this.interact();
    },

    data() {
        return {
            buttons: [],
            grids: [],
            buttonImages: [],
            productTypes: this.$store.state.appConfig.product.types,
            activeProductType: this.$store.state.appConfig.product.types[0],
            activeCategory: null,
            disableButtonMenus: false,
            draggedButtonId: null,
            selectedButton: null,

            popovers: {
                addProduct: false,
                addCategory: false
            },

            categories: [],
            products: [],
            productToAdd: null,
            categoryToAdd: null
        };
    },

    computed: {
        addedCategoryButtons() {
            return _.filter(this.buttons, { type: this.activeProductType, product: null });
        },

        availableCategories() {
            let categories = _.filter(this.categories, { type: this.activeProductType });
            return _.differenceBy(categories, _.map(this.addedCategoryButtons, 'category'), 'id');
        },

        addedProductButtons() {
            if (!this.activeCategory) return [];
            return _.filter(_.reject(this.buttons, { product: null }), {
                type: this.activeProductType,
                category_id: this.activeCategory.id
            });
        },

        availableProducts() {
            if (!this.activeCategory) return [];
            let products = this.products.filter(p => p.category_id === this.activeCategory.id)

            return _.differenceBy(products, _.map(this.addedProductButtons, 'product'), 'id');
        },

        lastRowColumnCount() {
            let gridIndex = this.productTypes.indexOf(this.activeProductType);
            let grid = this.grids[gridIndex];

            let filteredCategoryButtons = [];
            let filteredProductButtons = [];

            filteredProductButtons = _.filter(_.reject(this.buttons, { product: null }), {
                type: this.activeProductType
            });
            filteredCategoryButtons = _.filter(this.buttons, { type: this.activeProductType, product: null });

            return {
                categories: {
                    last_row: _.filter(filteredCategoryButtons, { row_num: grid['categories_row_count'] }).length,
                    last_column: _.filter(filteredCategoryButtons, { column_num: grid['categories_column_count'] })
                        .length
                },
                products: {
                    last_row: _.filter(filteredProductButtons, { row_num: grid['products_row_count'] }).length,
                    last_column: _.filter(filteredProductButtons, { column_num: grid['products_column_count'] }).length
                }
            };
        }
    },

    methods: {
        onDataLoaded(data) {
            this.categories = data.categories;
            this.products = data.products;
            this.buttons = data.buttons;
            this.grids = data.grids;
            this.buttonImages = data.buttonImages;

            let activeTypeCategoryButtons = _.sortBy(
                _.filter(this.buttons, { type: this.activeProductType, product: null }),
                ['row_num', 'column_num']
            );
            this.activeCategory = activeTypeCategoryButtons.length ? activeTypeCategoryButtons[0].category : null;
            this.selectedButton = activeTypeCategoryButtons.length ? activeTypeCategoryButtons[0] : null;
        },

        /**
         * Select Product or Category button
         */
        selectButton(button) {
            if (this.draggedButtonId) return;

            this.selectedButton = null;
            this.activeCategory = null;

            this.$nextTick(() => {
                this.selectedButton = button;
                this.activeCategory = button.category;
            });
        },

        /**
         * Get Category Button
         */
        getCategoryButton(productType, row, column) {
            let filteredButtons = _.filter(this.buttons, {
                type: productType,
                row_num: row,
                column_num: column,
                product: null
            });
            return filteredButtons.length ? filteredButtons[0] : null;
        },

        /**
         * Get Product Button
         */
        getProductButton(productType, row, column) {
            if (!this.activeCategory) {
                return null;
            }

            let filteredButtons = _.filter(_.reject(this.buttons, { product: null }), {
                type: productType,
                row_num: row,
                column_num: column,
                category_id: this.activeCategory.id
            });
            return filteredButtons.length ? filteredButtons[0] : null;
        },

        /**
         * Increment or decrement number of columns
         * or rows for category or product tables
         */
        updateGridSize(section, type, count) {
            let gridIndex = this.productTypes.indexOf(this.activeProductType);
            let keyToUpdate = `${section}_${type}_count`;
            let grid = this.grids[gridIndex];

            if (grid[keyToUpdate] + count > 0) {
                grid[keyToUpdate] += count;
            }

            ApiService.patch(`/admin/grids/${grid.id}`, grid).then(response => (this.grids[gridIndex] = grid));
        },

        /**
         *  Add new Category
         */
        addCategory(category) {
            let availableField = this.nextAvailableField('categories');

            if(!availableField) return

            let newButton = this.createButton(
                this.selectedButton
                    ? this.selectedButton
                    : this.addedCategoryButtons.length
                        ? this.addedCategoryButtons[this.addedCategoryButtons.length - 1]
                        : null
            );

            newButton.category_id = category.id;
            newButton.category = category;
            newButton.type = category.type;
            newButton.text = category.name;
            newButton.product_id = null;
            newButton.product = null;
            newButton.row_num = availableField.row_num;
            newButton.column_num = availableField.column_num;

            this.$nextTick(() => {
                this.categoryToAdd = null;
                this.$refs.selCategory.blur();
            });

            ApiService.post('/admin/buttons', newButton).then(response => {
                newButton = response.data;
                this.buttons.push(newButton);
                this.selectedButton = newButton;
                this.activeCategory = newButton.category;
            });
        },

        /**
         *  Add new Category
         */
        addProduct(product) {
            let availableField = this.nextAvailableField('products');

            if(!availableField) return

            let newButton = this.createButton(
                this.selectedButton
                    ? this.selectedButton
                    : this.addedProductButtons.length
                        ? this.addedProductButtons[this.addedProductButtons.length - 1]
                        : null
            );

            newButton.category_id = product.category_id;
            newButton.category = product.category;
            newButton.type = product.category.type;
            newButton.text = product.name;
            newButton.product_id = product.id;
            newButton.product = product;
            newButton.row_num = availableField.row_num;
            newButton.column_num = availableField.column_num;

            this.$nextTick(() => {
                this.productToAdd = null;
                this.$refs.selProduct.blur();
            });

            ApiService.post('/admin/buttons', newButton).then(response => {
                newButton = response.data;
                this.buttons.push(newButton);
            });
        },

        /**
         * Update a button
         */
        updateButton(button) {
            ApiService.patch(`/admin/buttons/${button.id}`, button);
        },

        /**
         * Delete a button
         */
        deleteButton(button) {
            let buttonsToDelete = [button];
            let updateCategory = false;

            if (button.product_id == null) {
                if (this.activeCategory.id == button.category_id) {
                    updateCategory = true;
                }

                let productButtons = _.filter(_.reject(this.buttons, { product: null }), {
                    category_id: button.category_id
                });
                buttonsToDelete = buttonsToDelete.concat(productButtons);
            }

            ApiService.delete(`/admin/buttons/${button.id}`, {}).then(response => {
                this.buttons = _.reject(this.buttons, button => _.map(buttonsToDelete, 'id').includes(button.id));

                if (updateCategory) {
                    // choose and select other category
                    let activeTypeCategoryButtons = _.sortBy(
                        _.filter(this.buttons, { type: this.activeProductType, product: null }),
                        ['row_num', 'column_num']
                    );
                    this.activeCategory = activeTypeCategoryButtons.length
                        ? activeTypeCategoryButtons[0].category
                        : null;
                    this.selectedButton = activeTypeCategoryButtons.length ? activeTypeCategoryButtons[0] : null;
                }
            });
        },

        /**
         * Create new button either based on existing button of from scratch
         */
        createButton(buttonToCopy = null) {
            let newButton = buttonToCopy
                ? JSON.parse(JSON.stringify(buttonToCopy))
                : {
                      background_color: '#3f9eff',
                      column_num: 0,
                      image: null,
                      image_fit: 0,
                      image_halign: 'center',
                      image_stretch: 0,
                      image_valign: 'center',
                      row_num: 0,
                      text: null,
                      text_halign: 'center',
                      text_size: '12px',
                      text_valign: 'center',
                      type: this.activeProductType
                  };

            newButton.row_num = 0;
            newButton.column_num = 0;
            newButton.product = null;
            newButton.product_id = null;
            newButton.category = null;
            newButton.category_id = null;
            delete newButton.id;

            return newButton;
        },

        /**
         * Find next available field to place a button
         */
        nextAvailableField(section) {
            let gridIndex = this.productTypes.indexOf(this.activeProductType);
            let columns = this.grids[gridIndex][`${section}_column_count`];
            let rows = this.grids[gridIndex][`${section}_row_count`];

            let addedButtons = section == 'categories' ? this.addedCategoryButtons : this.addedProductButtons;
            for (let row = 1; row <= rows; row++) {
                for (let column = 1; column <= columns; column++) {
                    if (_.filter(addedButtons, { row_num: row, column_num: column }).length == 0) {
                        return { row_num: row, column_num: column };
                    }
                }
            }

            return null;
        },

        /**
         * Initiate drag & drop functionality
         */
        interact() {
            let dropContainerOptions = {
                // Require a 55% element overlap for a drop to be possible
                overlap: 0.55,

                // on drop activate mark all containers where button can be dropped
                ondropactivate: event => {
                    event.target.classList.add('drop-active');
                },

                // on drag enter add drop possible mark to this container
                ondragenter: event => {
                    event.target.classList.add('drop-target');
                },

                // on drag leave remove the drop possible mark on a container
                ondragleave: event => {
                    event.target.classList.remove('drop-target');
                },

                // on drop move the button the the new container
                ondrop: event => {
                    this.dropButton(event.target, event.relatedTarget);
                },

                // on drop deactivate remove drop containers mark
                ondropdeactivate: event => {
                    event.target.classList.remove('drop-active');
                }
            };

            let draggableOptions = {
                // enable autoScroll
                autoScroll: true,

                // on drag start mark the button being dragged and detect the drag section
                onstart: event => {
                    this.draggedButtonId = event.target.id;
                },

                // on every dragmove event update the coordinates of the dragged button
                onmove: event => {
                    let target = event.target;

                    // keep the dragged position in the data-x/data-y attributes
                    let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
                    let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

                    // translate the element
                    target.style.webkitTransform = target.style.transform = 'translate(' + x + 'px, ' + y + 'px)';

                    // update the posiion attributes
                    target.setAttribute('data-x', x);
                    target.setAttribute('data-y', y);
                },

                // on drag end remove attributes added to the dragged button
                onend: event => {
                    event.target.removeAttribute('data-x');
                    event.target.removeAttribute('data-y');
                    event.target.style.transform = 'translate(0px, 0px)';
                    this.draggedButtonId = null;
                }
            };

            // enable draggables to be dropped into this
            interact('.drop-container-category').dropzone(
                Object.assign({ accept: '.draggable.category' }, dropContainerOptions)
            );
            interact('.drop-container-product').dropzone(
                Object.assign({ accept: '.draggable.product' }, dropContainerOptions)
            );

            // set up elements that can be dragged
            interact('.draggable.category').draggable(
                Object.assign({ restrict: { restriction: '.category-container', endOnly: true } }, draggableOptions)
            );
            interact('.draggable.product').draggable(
                Object.assign({ restrict: { restriction: '.product-container', endOnly: true } }, draggableOptions)
            );
        },

        /**
         * Drop the button to the specified container
         * @param dropContainer
         * @param draggedButton
         */
        dropButton(dropContainer, draggedButton) {
            let sourceRow = parseInt(draggedButton.id.split('-')[0]);
            let sourceColumn = parseInt(draggedButton.id.split('-')[1]);
            let sourceSection = draggedButton.id.split('-')[2];

            let targetRow = parseInt(dropContainer.id.split('-')[0]);
            let targetColumn = parseInt(dropContainer.id.split('-')[1]);

            let sourceBtn =
                sourceSection == 'categorybtn'
                    ? this.getCategoryButton(this.activeProductType, sourceRow, sourceColumn)
                    : this.getProductButton(this.activeProductType, sourceRow, sourceColumn);
            let targetBtn =
                sourceSection == 'categorybtn'
                    ? this.getCategoryButton(this.activeProductType, targetRow, targetColumn)
                    : this.getProductButton(this.activeProductType, targetRow, targetColumn);

            if (targetBtn) {
                targetBtn.row_num = 0;
                targetBtn.column_num = 0;
            }

            this.$nextTick(() => {
                sourceBtn.row_num = targetRow;
                sourceBtn.column_num = targetColumn;
                this.updateButton(sourceBtn);

                this.$nextTick(() => {
                    if (targetBtn) {
                        targetBtn.row_num = sourceRow;
                        targetBtn.column_num = sourceColumn;
                        this.updateButton(targetBtn);
                    }
                });
            });
        },

        autogenerate() {
            this.$confirm(
                this.$t('order_settings.autogenerate_message'),
                {
                    buttonCancelText: this.$t('general.cancel'),
                    buttonTrueText: this.$t('general.yes'),
                    buttonFalseText: this.$t('general.no'),
                    icon: 'fal fa-cogs',
                    color: 'error',
                    title: this.$t('order_settings.autogenerate')
                },
                result => {
                    if (result === null) return;

                    ApiService.post('/admin/buttons/generate', { clearOld: result }).then(response => {
                        this.grids = response.data.grids;
                        this.buttons = response.data.buttons;
                    });
                }
            );
        }
    }
};
</script>

<style lang="scss">
.draggable {
    touch-action: none;
}

.sales-interface {
    min-width: 300px;
    height: 100%;

    .v-card__text {
        height: 100% !important;
        position: relative;
        display: flex;
        flex-direction: column;

        .heading {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: wrap-reverse;

            .v-tabs {
                width: initial;

                .v-tab {
                    margin-left: 0 !important;
                }
            }

            .buttons {
                display: flex;
                align-items: center;
                z-index: 1;
                height: 50px;
                margin-top: 5px;
            }
        }

        .product-type-tabs {
            position: relative;
            height: 100%;
            width: 100%;
            overflow: visible;

            .v-window__container {
                height: 100%;
            }

            .product-type-tab {
                height: 100%;
                display: flex;
                flex-direction: column;

                .category-container {
                    position: relative;
                    flex: 1;

                    table {
                        width: 100%;
                        height: 100%;
                        table-layout: fixed;
                        border-spacing: 7px;
                        border-collapse: separate;
                    }
                }

                .product-container {
                    position: relative;
                    flex: 3;

                    table {
                        width: 100%;
                        height: 100%;
                        table-layout: fixed;
                        border-spacing: 7px;
                        border-collapse: separate;
                    }
                }

                .draggable {
                    .v-btn {
                        border-radius: 0px;
                    }
                }

                .drag-active {
                    opacity: 0.7;
                    z-index: 10000;
                    height: 80%;
                    width: 80%;
                }

                .drop-container-category {
                    transition: background-color 0.3s;
                    outline: 1px solid #ff9800;

                    &.drop-active {
                        background: #e7e7e7;
                        outline: 1px solid green;

                        &.drop-target {
                            background-color: #29e;
                        }
                    }
                }

                .drop-container-product {
                    transition: background-color 0.3s;
                    outline: 1px solid #2f404f;

                    &.drop-active {
                        background: #e7e7e7;
                        outline: 1px solid green;

                        &.drop-target {
                            background-color: #29e;
                        }
                    }
                }

                .disabled {
                    opacity: 0.3;
                }

                .grid-settings {
                    position: absolute;
                    top: -2px;
                    right: 0;
                    z-index: 5;
                    padding: 2px;
                    background: #29333fd1;
                    border-radius: 2px;
                    color: #f3f3f4;

                    .row-column {
                        display: flex;
                        justify-content: space-between;

                        .grid-btn {
                            width: 25px;
                            height: 25px;
                            padding: 0;
                            margin: 0;
                            min-width: 0;
                        }

                        .label {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 12px;
                        }
                    }
                }
            }
        }
    }
}
</style>
